import { ErrorHandler, Injectable, NgZone } from "@angular/core";
import { DialogMessageService } from "./services/dialog-message.service";
import { ResponseError } from "./models/response-error";
import { HttpErrorResponse } from "@angular/common/http";
import { ApplicationInsightsService } from "./services/application-insights.service";

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private _errorQueue: string[] = [];
  private _lastErrorTime: number = 0;
  private _lastErrorMessage: string = '';
  private _errorCount: number = 0;
  private _isShowingError: boolean = false;
  private readonly _IGNORED_BROWSER_ERROR_MESSAGES: string[] = [
    'ResizeObserver loop completed with undelivered notifications.',
    'ResizeObserver loop limit exceeded',
  ];
  private readonly _THROTTLE_TIME_MS: number = 1000; // 1 secondo tra un errore e l'altro
  private readonly _MAX_ERRORS_PER_MINUTE: number = 5;
  private readonly _RESET_INTERVAL_MS: number = 60000; // 1 minuto

  constructor(
    private readonly _dialogMessage: DialogMessageService,
    private readonly _zone: NgZone,
    private readonly _appInsights: ApplicationInsightsService,
  ) {
    // Reset contatore errori ogni minuto
    setInterval(() => {
      this._errorCount = 0;
    }, this._RESET_INTERVAL_MS);
  }

  handleError(error: any) {
    if (this._shouldIgnoreError(error)) {
      console.warn('GlobalErrorHandler - Errore browser ignorato:', error);
      return;
    }

    if (!(error instanceof ResponseError)
      && !(error instanceof HttpErrorResponse)) {
      if (error.message
        && (<string>error.message).startsWith('NG0100') //ExpressionChangedAfterItHasBeenCheckedError
      ) {
        //skip
        return;
      }

      // Throttling: controlla se è passato abbastanza tempo dall'ultimo errore
      const now = Date.now();
      const errorMessage = this._getErrorMessage(error);

      // Controllo duplicati: ignora errori identici consecutivi
      if (errorMessage === this._lastErrorMessage) {
        console.warn('GlobalErrorHandler - Errore duplicato ignorato:', error);
        return;
      }

      if (now - this._lastErrorTime < this._THROTTLE_TIME_MS) {
        console.warn('GlobalErrorHandler - Errore ignorato per throttling:', error);
        return;
      }

      // Limite errori: controlla se abbiamo superato il limite di errori al minuto
      if (this._errorCount >= this._MAX_ERRORS_PER_MINUTE) {
        console.error('GlobalErrorHandler - Limite errori raggiunto. Errore non mostrato:', error);
        return;
      }

      // Previeni mostrare errori se uno è già in corso
      if (this._isShowingError) {
        console.warn('GlobalErrorHandler - Errore accodato:', error);
        return;
      }

      this._lastErrorTime = now;
      this._lastErrorMessage = errorMessage;
      this._errorCount++;
      this._isShowingError = true;

      this._trackError(error, errorMessage);

      this._zone.run(() => {
        this._dialogMessage.showError(errorMessage);
        // Reset flag dopo un breve delay per permettere la chiusura del dialog
        setTimeout(() => {
          this._isShowingError = false;
        }, 500);
      });
    }
    console.error('GlobalErrorHandler - ', error);
  }

  private _getErrorMessage(error: any): string {
    if (error instanceof TypeError) {
      return error.message + ' - ' + error.stack;
    }
    return error.rejection ?? error;
  }

  private _shouldIgnoreError(error: unknown): boolean {
    const candidateMessages = [
      this._extractErrorText(error),
      this._extractErrorText((error as { cause?: unknown } | null)?.cause),
      this._extractErrorText((error as { error?: unknown } | null)?.error),
      this._extractErrorText((error as { rejection?: unknown } | null)?.rejection),
    ].filter((message): message is string => Boolean(message));

    return candidateMessages.some((message) =>
      this._IGNORED_BROWSER_ERROR_MESSAGES.some((ignoredMessage) => message.includes(ignoredMessage)),
    );
  }

  private _extractErrorText(value: unknown): string | null {
    if (typeof value === 'string') {
      return value;
    }

    if (value instanceof Error) {
      return value.message;
    }

    if (value && typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
      return value.message;
    }

    return null;
  }

  private _trackError(rawError: unknown, rawMessage: unknown): void {
    try {
      const messageText = typeof rawMessage === 'string' ? rawMessage : this._safeStringify(rawMessage);
      const errorForTelemetry = rawError instanceof Error ? rawError : new Error(messageText);

      const properties: Record<string, unknown> = {
        message: messageText,
        timestamp: new Date().toISOString(),
      };

      if (rawError instanceof Error && rawError.stack) {
        properties['stack'] = rawError.stack;
      }

      this._appInsights.trackException(errorForTelemetry, 3, properties);
    } catch {
      // suppressed
    }
  }

  private _safeStringify(value: unknown): string {
    if (value === undefined) {
      return 'undefined';
    }

    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
}
